import * as React from 'react'
import { ReactElement } from 'react'
import { AttributeDefinitionDto, AttributeDto, AttributeType, AttributeViewType } from '../../../hooks/useAttributes'
import { Checkbox, InfoLabel, Input, mergeClasses } from '@fluentui/react-components'
import BaseCombobox from '../../Combobox/BaseCombobox'
import LocalDatePicker from '../../DatePicker/LocalDatePicker'
import MoneyInput from '../../Input/MoneyInput'
import { CommitEventType } from '../../Input/FormattableNumberInput'
import Textarea from '../../Text/TextArea'
import { useSettings } from '../../../hooks/useSettings'
import { DateTime } from 'luxon'
import { InputProps } from '@fluentui/react-input'
import { AttributesListProps, isAttributeValueNonNull, useAttributesStyles } from './AttributesList'
import { useTranslation } from 'react-i18next'

export type KeyValue = {
    key: string
    value: string
}

export type AttributesListRowProps = AttributesListProps & {
    definition: AttributeDefinitionDto
}

const getValue = (props: AttributesListRowProps) => {
    return (fallbackValue?: any) =>
        props.entity.attributes?.find(attribute => attribute.definitionId === props.definition.id)?.value ??
        (props.viewType !== AttributeViewType.Filter && !props.entityId
            ? props.definition.properties?.initialValue
            : undefined) ??
        fallbackValue
}

const setValue = (props: AttributesListRowProps) => {
    return (newValue: any) => {
        if (!props.entity.attributes) {
            props.entity.attributes = []
            props.setFieldValue?.('attributes', [])
        }

        let index: number = props.entity.attributes?.findIndex(
            attribute => attribute.definitionId === props.definition.id,
        )
        let attribute: AttributeDto = props.entity.attributes?.find(
            attribute => attribute.definitionId === props.definition.id,
        )

        const valueToSet = isAttributeValueNonNull(newValue) ? newValue : undefined
        // Add attribute, when needed, and set value
        if (!!valueToSet && attribute === undefined) {
            attribute = {
                entityId: props.entityId,
                definitionId: props.definition.id,
                entityType: props.entityType,
                value: undefined,
            }
            props.entity.attributes?.push(attribute)
            index = props.entity.attributes?.findIndex(attribute => attribute.definitionId === props.definition.id)
        }
        attribute.value = valueToSet
        props.setFieldValue?.(`attributes[${index}].value`, valueToSet)

        if (props.entity.attributes && props.entity.attributes.length > 0) {
            props.entity.attributes = props.entity.attributes?.filter(attribute => attribute.value !== undefined)
            props.setFieldValue?.('attributes', props.entity.attributes)
        }
    }
}

/**
 * Render a component based on the given props. Sort the selected Combobox options so the server will always receive the
 * selected options in the same order. This way this component is also usable for filtering (FileOverview) because the
 * queries used (AttributeRepositoryCustom) won't have to apply sorting.
 *
 * @param props
 */
export default function AttributeDefinitionRow(props: AttributesListRowProps): ReactElement {
    const settings = useSettings()
    const styles = useAttributesStyles()
    const { t: translate } = useTranslation()

    const getter = getValue(props)
    const setter = setValue(props)

    if (props.definition.dataType === AttributeType.Boolean) {
        return (
            <InfoLabel
                className={mergeClasses(styles.fieldLabel, !props.definition.enabled && styles.fieldLabelDisabled)}
                info={translate('attribute.field.boolean.explanation').replaceAll('\n', '<br/>')}
            >
                <>
                    <Checkbox
                        checked={getter('mixed')}
                        onChange={(_, d) => setter(d.checked && getter('mixed') === false ? 'mixed' : d.checked)}
                        {...props.definition.properties}
                    />
                    <small>{getter('mixed') === true ? 'Ja' : getter('mixed') === false ? 'Nee' : ''}</small>
                </>
            </InfoLabel>
        )
    } else if (props.definition.dataType === AttributeType.KeyValueList) {
        return (
            <BaseCombobox<KeyValue>
                entities={props.definition.properties?.entities as KeyValue[]}
                getEntityValue={entity => entity.key}
                getEntityLabel={entity => entity.value}
                alphabetical
                selectedOptions={getter([])}
                onOptionSelect={(_, d) => setter(d.selectedOptions?.sort((a, b) => a.localeCompare(b)))}
                {...props.definition.properties}
            />
        )
    } else if (props.definition.dataType === AttributeType.LocalDate) {
        return (
            <LocalDatePicker
                isoValue={getter(
                    props.viewType !== AttributeViewType.Filter &&
                        !props.entityId &&
                        props.definition.properties?.useToday
                        ? DateTime.now().toISODate()
                        : null,
                )}
                onSelectDate={setter}
                {...props.definition.properties}
            />
        )
    } else if (props.definition.dataType === AttributeType.Money) {
        return (
            <MoneyInput
                value={getter('')}
                currency={props.entity.currency || settings?.fileScaffold?.currency || 'EUR'}
                onCommit={(eventType: CommitEventType, value: string) => {
                    if (eventType !== CommitEventType.OnLoad) {
                        setter(value)
                    }
                }}
                {...props.definition.properties}
            />
        )
    } else if (props.definition.dataType === AttributeType.Text) {
        return (
            <Textarea
                value={getter('')}
                onChange={(_, d) => setter(d.value)}
                autoAdjustHeight
                {...props.definition.properties}
            />
        )
    } else {
        let type: InputProps['type'] = 'text'
        if (props.definition.dataType === AttributeType.Number) {
            type = 'number'
        } else if (props.definition.dataType === AttributeType.String && props.definition.properties?.type) {
            type = props.definition.properties?.type
        }

        return (
            <Input
                value={getter('')}
                onChange={(_, d) => setter(d.value)}
                type={type}
                {...props.definition.properties}
            />
        )
    }
}
